import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import MovieIcon from "@mui/icons-material/Movie";
import UploadIcon from "@mui/icons-material/Upload";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  CreateVideoFileUploadMutation,
  FinishVideoFileUploadMutation,
  VideoFileQuery,
} from "@/shared/graphql";

export type VideoFileStatus =
  | "UPLOADING"
  | "QUEUED"
  | "PROCESSING"
  | "READY"
  | "FAILED";

interface VideoFileData {
  id: string;
  status: VideoFileStatus;
  fileName: string;
  playlistUrl?: string | null;
  posterUrl?: string | null;
  error?: string | null;
}

interface VideoFileQueryData {
  videoFile?: VideoFileData | null;
}

interface VideoFileUploadHeader {
  name: string;
  value: string;
}

interface CreateVideoFileUploadData {
  createVideoFileUpload: {
    videoFile: { id: string; status: VideoFileStatus; fileName: string };
    uploadUrl: string;
    headers: VideoFileUploadHeader[];
  };
}

/**
 * Предел размера исходника. Дублирует серверный `videoFiles.maxSizeBytes`
 * (300 МБ): проверяем до запроса, чтобы не гнать сотни мегабайт в presigned
 * PUT ради ответа TOO_LARGE.
 */
const MAX_VIDEO_SIZE_BYTES = 314572800;

/** Пока идёт транскодинг, статус спрашиваем раз в 5 секунд. */
const POLL_INTERVAL_MS = 5000;

const FINAL_STATUSES: VideoFileStatus[] = ["READY", "FAILED"];

const STATUS_CHIP_COLORS: Record<
  VideoFileStatus,
  "default" | "success" | "error"
> = {
  UPLOADING: "default",
  QUEUED: "default",
  PROCESSING: "default",
  READY: "success",
  FAILED: "error",
};

interface VideoFileFieldProps {
  label: string;
  value?: string | null;
  onChange: (id: string | null) => void;
  helperText?: string;
}

export function VideoFileField({
  label,
  value,
  onChange,
  helperText,
}: VideoFileFieldProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    return () => {
      cancelledRef.current = true;
      xhrRef.current?.abort();
    };
  }, []);

  // Коды ошибок приходят и от мутаций (message равен коду), и от самого
  // VideoFile.error. Показываем только переведённый текст: у кода без перевода
  // падаем на NETWORK, чтобы наружу не утекло сырое сообщение исключения.
  const translateError = useCallback(
    (code: string) => {
      const key = `videoFiles.errors.${code}`;
      const text = t(key);

      return text === key ? t("videoFiles.errors.NETWORK") : text;
    },
    [t]
  );

  const [createVideoFileUpload] = useMutation<CreateVideoFileUploadData>(
    CreateVideoFileUploadMutation
  );
  const [finishVideoFileUpload] = useMutation(FinishVideoFileUploadMutation);

  const { data, startPolling, stopPolling } = useQuery<VideoFileQueryData>(
    VideoFileQuery,
    {
      variables: { id: value },
      skip: !value,
      fetchPolicy: "cache-and-network",
    }
  );

  const videoFile = value ? data?.videoFile : null;
  const status = videoFile?.status;

  useEffect(() => {
    if (!value) {
      return undefined;
    }

    if (status && FINAL_STATUSES.includes(status)) {
      stopPolling();
      return undefined;
    }

    startPolling(POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [value, status, startPolling, stopPolling]);

  const putFile = (
    uploadUrl: string,
    headers: VideoFileUploadHeader[],
    file: File
  ) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      xhr.open("PUT", uploadUrl);
      headers.forEach((header) =>
        xhr.setRequestHeader(header.name, header.value)
      );

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        xhrRef.current = null;

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error("NETWORK"));
        }
      };

      xhr.onerror = () => {
        xhrRef.current = null;
        reject(new Error("NETWORK"));
      };

      xhr.onabort = () => {
        xhrRef.current = null;
        reject(new Error("NETWORK"));
      };

      xhr.send(file);
    });

  const upload = async (file: File) => {
    if (!file.type.startsWith("video/")) {
      enqueueSnackbar(translateError("UNSUPPORTED"), { variant: "error" });
      return;
    }

    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      enqueueSnackbar(translateError("TOO_LARGE"), { variant: "error" });
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const result = await createVideoFileUpload({
        variables: {
          input: {
            fileName: file.name,
            size: file.size,
            mimeType: file.type,
          },
        },
      });

      const created = result.data?.createVideoFileUpload;

      if (!created) {
        throw new Error("UPLOAD_FAILED");
      }

      await putFile(created.uploadUrl, created.headers, file);
      await finishVideoFileUpload({
        variables: { id: created.videoFile.id },
      });

      if (cancelledRef.current) {
        return;
      }

      onChange(created.videoFile.id);
    } catch (error) {
      if (cancelledRef.current) {
        return;
      }

      // Кодом считаем только строку кодовой формы (UNSUPPORTED, TOO_LARGE):
      // всё остальное это текст самого исключения (Failed to fetch, серверная
      // фраза на чужом языке), пользователю его показывать нельзя.
      const raw = error instanceof Error ? error.message : "";
      const code = /^[A-Z][A-Z0-9_]*$/.test(raw) ? raw : "NETWORK";

      enqueueSnackbar(translateError(code), { variant: "error" });
    } finally {
      if (!cancelledRef.current) {
        setUploading(false);
        setProgress(0);
      }
    }
  };

  const handlePick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    inputRef.current?.click();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Сбрасываем значение инпута, иначе повторный выбор того же файла
    // не даст события change.
    event.target.value = "";

    if (file) {
      void upload(file);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDrag(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDrag(false);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDrag(false);

    const file = event.dataTransfer.files[0];

    if (file) {
      void upload(file);
    }
  };

  const handleRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // Сам файл не удаляем: он может быть выбран другой обложкой.
    onChange(null);
  };

  const errorText = videoFile?.error ? translateError(videoFile.error) : null;

  return (
    <>
      <FormControl
        component="fieldset"
        fullWidth
        sx={{
          border: "1px solid",
          borderColor: theme.palette.divider,
          borderRadius: 1,
          padding: 2,
          position: "relative",
          "&:hover": {
            borderColor: theme.palette.text.secondary,
          },
        }}
      >
        {label && (
          <FormLabel
            component="legend"
            sx={{
              float: "unset",
              display: "block",
              width: "auto",
              padding: "0 5px",
              height: "11px",
              fontSize: "0.75em",
              top: -3,
              maxWidth: "100%",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </FormLabel>
        )}

        {!value ? (
          <ButtonBase
            sx={{
              width: "100%",
              minHeight: 120,
              backgroundColor: "rgba(0, 0, 0, 0.23)",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "3rem",
              color: drag ? "success.main" : "text.disabled",
              border: "2px dashed",
              borderColor:
                drag || uploading ? "#2e7d32" : "rgba(0, 0, 0, 0.38)",
              gap: 1,
              p: 2,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.1)",
              },
            }}
            disabled={uploading}
            onClick={handlePick}
            onDragOverCapture={handleDragOver}
            onDragEnterCapture={handleDragEnter}
            onDragLeaveCapture={handleDragLeave}
            onDropCapture={handleDrop}
          >
            {uploading ? (
              <Box sx={{ width: "100%", pointerEvents: "none" }}>
                <Typography variant="body2" color="text.secondary">
                  {t("videoFiles.uploading", { percent: progress })}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  color="success"
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <>
                <UploadIcon
                  fontSize="inherit"
                  color="inherit"
                  sx={{ pointerEvents: "none" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t("videoFiles.choose")}
                </Typography>
              </>
            )}
          </ButtonBase>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              borderRadius: 1,
            }}
          >
            {videoFile?.posterUrl ? (
              <Box
                component="img"
                src={videoFile.posterUrl}
                alt=""
                sx={{
                  width: 96,
                  height: 54,
                  objectFit: "cover",
                  borderRadius: 1,
                  display: "block",
                }}
              />
            ) : (
              <MovieIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {videoFile?.fileName}
              </Typography>

              {status && (
                <Chip
                  size="small"
                  color={STATUS_CHIP_COLORS[status]}
                  label={t(`videoFiles.status.${status}`)}
                  sx={{ mt: 0.5 }}
                />
              )}

              {errorText && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {errorText}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <input
          accept="video/*"
          type="file"
          style={{
            position: "absolute",
            opacity: 0,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
          }}
          ref={inputRef}
          onChange={handleInputChange}
        />

        {value && (
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              onClick={handlePick}
              variant="outlined"
              disabled={uploading}
              sx={{ flex: 1 }}
            >
              {t("videoFiles.replace")}
            </Button>
            <Button onClick={handleRemove} color="inherit" disabled={uploading}>
              {t("videoFiles.remove")}
            </Button>
          </Box>
        )}

        {value && uploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("videoFiles.uploading", { percent: progress })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              color="success"
              sx={{ mt: 1 }}
            />
          </Box>
        )}
      </FormControl>

      {helperText && (
        <FormHelperText
          style={{
            marginTop: theme.spacing(0.5),
            marginRight: theme.spacing(2),
            marginBottom: 0,
            marginLeft: theme.spacing(2),
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </>
  );
}
